import { AuthenticationResult, IPublicClientApplication, SilentRequest } from "@azure/msal-browser";

/**
 * Token Acquisition Queue - Prevents race conditions with MSAL cache
 * 
 * Problem: When multiple HTTP requests fire simultaneously (common for new users),
 * multiple acquireTokenSilent() calls hit the MSAL cache at the same time, causing:
 * "BrowserAuthError: no_token_request_cache_error"
 * 
 * Solution: Queue token requests so only one acquireTokenSilent() runs at a time.
 * Concurrent requests for the same scopes/account get the same promise.
 */

interface QueuedTokenRequest {
    promise: Promise<AuthenticationResult>;
    silentRequest: SilentRequest;
}

class TokenAcquisitionQueue {
    private queue: Map<string, QueuedTokenRequest> = new Map();
    private pending: Set<string> = new Set();
    // Must be shorter than MSAL's own iframe timeout (default 30 s in v5) so we
    // throw a typed error before MSAL silently drops the request.
    private readonly ACQUISITION_TIMEOUT_MS = 25_000;

    /**
     * Generate a cache key based on account OID and scopes
     */
    private getCacheKey(accountOid: string | undefined, scopes: string[]): string {
        const scopeStr = scopes.sort().join("|");
        return `${accountOid || "unknown"}:${scopeStr}`;
    }

    /**
     * Acquire a token silently with deduplication
     * If an identical request is already in flight, return the same promise
     */
    async acquireTokenSilent(
        msalInstance: IPublicClientApplication,
        silentRequest: SilentRequest
    ): Promise<AuthenticationResult> {
        const accountOid = silentRequest.account?.homeAccountId;
        const scopes = silentRequest.scopes || [];
        const cacheKey = this.getCacheKey(accountOid, scopes);

        // If an identical request is already queued, return its promise
        if (this.queue.has(cacheKey)) {
            console.debug(
                `[TokenQueue] Reusing in-flight token request for scopes: ${scopes.join(", ")}`
            );
            return this.queue.get(cacheKey)!.promise;
        }

        // Mark this request as pending
        this.pending.add(cacheKey);

        // Create the actual promise that will be shared
        const tokenPromise = (async () => {
            try {
                console.debug(
                    `[TokenQueue] Acquiring token for scopes: ${scopes.join(", ")}`
                );
                const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(
                        () => reject(new Error(`monitor_window_timeout: acquireTokenSilent timed out after ${this.ACQUISITION_TIMEOUT_MS}ms`)),
                        this.ACQUISITION_TIMEOUT_MS
                    )
                );
                const result = await Promise.race([
                    msalInstance.acquireTokenSilent(silentRequest),
                    timeoutPromise,
                ]);
                console.debug(`[TokenQueue] Token acquired successfully`);
                return result;
            } catch (error) {
                console.error(`[TokenQueue] Token acquisition failed:`, error);
                throw error;
            } finally {
                // Remove from queue after completion (success or error)
                this.queue.delete(cacheKey);
                this.pending.delete(cacheKey);
            }
        })();

        // Store in queue
        this.queue.set(cacheKey, {
            promise: tokenPromise,
            silentRequest
        });

        return tokenPromise;
    }

    /**
     * Check if there are any pending token requests
     */
    hasPendingRequests(): boolean {
        return this.pending.size > 0;
    }

    /**
     * Wait for all pending requests to complete
     * Useful for initialization scenarios
     */
    async waitForPending(): Promise<void> {
        while (this.hasPendingRequests()) {
            const promises = Array.from(this.queue.values()).map(item => item.promise);
            if (promises.length > 0) {
                await Promise.allSettled(promises);
            }
        }
    }

    /**
     * Clear the queue (for testing or emergency scenarios)
     */
    clear(): void {
        this.queue.clear();
        this.pending.clear();
    }
}

// Export singleton instance
export const tokenAcquisitionQueue = new TokenAcquisitionQueue();
